import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { LeadsTable } from "../components/leads/LeadsTable";
import { BulkAssignModal } from "../components/leads/BulkAssignModal";
import { BulkUnassignModal } from "../components/leads/BulkUnassignModal";
import { CheckEmailsModal } from "../components/leads/CheckEmailsModal";
import { MultiSelect } from "../components/ui/multi-select";
import { UserPlus, UserMinus, RefreshCw, Users, Search, Filter, ChevronDown, ChevronUp, X, Mail } from "lucide-react";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger
} from "../components/ui/dropdown-menu";
import { toast } from "sonner";
import { fetchAvailableLeads, fetchDistinctValues } from "../services/crmServices/leadService";
import { useAuth } from "../hooks/useAuth";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { useSelector } from "react-redux";
import { selectLeadStatuses } from "../store/slices/leadStatusSlice";

export function LiveLeads() {
    const { user } = useAuth();
    const leadStatuses = useSelector(selectLeadStatuses);
    const [leads, setLeads] = useState([]);
    const [filteredLeads, setFilteredLeads] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedLeads, setSelectedLeads] = useState([]);
    const [selectAll, setSelectAll] = useState(false);
    const [showBulkAssignModal, setShowBulkAssignModal] = useState(false);
    const [showBulkUnassignModal, setShowBulkUnassignModal] = useState(false);
    const [showCheckEmailsModal, setShowCheckEmailsModal] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [pagination, setPagination] = useState(null);
    const [currentFilters, setCurrentFilters] = useState({});
    const [searchTerm, setSearchTerm] = useState("");
    const [showFilters, setShowFilters] = useState(false);
    
    // Filter options
    const [affiliateOptions, setAffiliateOptions] = useState([]);
    const [countryOptions, setCountryOptions] = useState([]);
    const [statusOptions, setStatusOptions] = useState([]);
    
    // All affiliates with counts (based on current filters, not just current page)
    const [allAffiliatesWithCounts, setAllAffiliatesWithCounts] = useState([]);

    // Add ref to track if component is mounted (for cleanup)
    const isMountedRef = useRef(true);

    // Check if user is admin
    useEffect(() => {
        if (user?.position?.role !== "admin") {
            toast.error("Access denied. Admin privileges required.");
        }
    }, [user]);

    // Load filter options
    useEffect(() => {
        const loadFilterOptions = async () => {
            try {
                const [affiliatesResponse, countriesResponse] = await Promise.all([
                    fetchDistinctValues('affiliate'),
                    fetchDistinctValues('country')
                ]);

                if (affiliatesResponse?.data) {
                    setAffiliateOptions(affiliatesResponse.data.map(aff => ({ label: aff, value: aff })));
                }
                if (countriesResponse?.data) {
                    setCountryOptions(countriesResponse.data.map(country => ({ label: country, value: country })));
                }
                
                // Set status options from Redux
                if (leadStatuses && leadStatuses.length > 0) {
                    setStatusOptions(leadStatuses.map(status => ({ 
                        label: typeof status === 'string' ? status : status.label || status.value, 
                        value: typeof status === 'string' ? status : status.value || status.label 
                    })));
                }
            } catch (error) {
                console.error("Error loading filter options:", error);
            }
        };

        loadFilterOptions();
    }, [leadStatuses]);

    // Fetch affiliate leads (leads with affiliate field) - OPTIMIZED: Server-side filtering
    const fetchLeadsFromServer = useCallback(async () => {
        setLoading(true);
        try {
            // Build filters - add hasAffiliate filter for server-side filtering
            const filters = {
                ...currentFilters,
                hasAffiliate: true, // Server-side filter for leads with affiliate field
                ...(searchTerm.trim() && { searchTerm: searchTerm.trim() })
            };

            // Remove undefined and empty values
            Object.keys(filters).forEach(key => {
                if (filters[key] === undefined || 
                    filters[key] === null ||
                    (Array.isArray(filters[key]) && filters[key].length === 0) ||
                    (typeof filters[key] === 'string' && filters[key].trim() === '')) {
                    delete filters[key];
                }
            });

            // Fetch leads with server-side filtering - use normal pageSize (no 3x multiplier)
            const response = await fetchAvailableLeads(currentPage, pageSize, filters);
            
            if (response?.data) {
                // No client-side filtering needed - backend handles it
                setLeads(response.data);
                setFilteredLeads(response.data);
                
                // Use backend pagination
                if (response.pagination) {
                    setPagination(response.pagination);
                } else {
                    setPagination({
                        page: currentPage,
                        limit: pageSize,
                        totalCount: response.data.length,
                        totalPages: Math.ceil(response.data.length / pageSize)
                    });
                }
            } else {
                setLeads([]);
                setFilteredLeads([]);
                setPagination(null);
            }
        } catch (error) {
            console.error("Error fetching affiliate leads:", error);
            toast.error("Failed to fetch affiliate leads");
            setLeads([]);
            setFilteredLeads([]);
            setPagination(null);
        } finally {
            setLoading(false);
        }
    }, [currentPage, pageSize, searchTerm, currentFilters]);

    useEffect(() => {
        fetchLeadsFromServer();
    }, [fetchLeadsFromServer]);

    // Handle select all
    const handleSelectAll = (checked) => {
        setSelectAll(checked);
        if (checked) {
            setSelectedLeads([...filteredLeads]);
        } else {
            setSelectedLeads([]);
        }
    };

    // Handle individual lead selection
    const handleLeadSelect = (lead, checked) => {
        if (checked) {
            setSelectedLeads([...selectedLeads, lead]);
        } else {
            setSelectedLeads(selectedLeads.filter(l => l._id !== lead._id));
        }
    };

    // Handle assignment success
    const handleAssignmentSuccess = () => {
        setSelectedLeads([]);
        setSelectAll(false);
        fetchLeadsFromServer();
    };

    // Handle bulk assign
    const handleBulkAssign = () => {
        if (selectedLeads.length === 0) {
            toast.error("Please select leads to assign");
            return;
        }
        setShowBulkAssignModal(true);
    };

    // Handle bulk unassign
    const handleBulkUnassign = () => {
        if (selectedLeads.length === 0) {
            toast.error("Please select leads to unassign");
            return;
        }
        setShowBulkUnassignModal(true);
    };

    // Handle unassignment success
    const handleUnassignmentSuccess = () => {
        setShowBulkUnassignModal(false);
        setSelectedLeads([]);
        setSelectAll(false);
        fetchLeadsFromServer();
    };

    // Handle edit lead
    const handleEdit = (lead) => {
        // Navigate to edit page or open edit modal
        window.location.href = `/leads/edit/${lead._id}`;
    };

    // Handle delete lead
    const handleDelete = (leadId) => {
        // You can add delete functionality here if needed
        // For now, just show a message
        toast.info("Delete functionality can be added if needed");
    };

    // Handle lead selection (for LeadsTable)
    const isLeadSelected = (leadId) => {
        return selectedLeads.some(lead => lead._id === leadId);
    };

    const handleSelectLead = (lead, checked) => {
        handleLeadSelect(lead, checked);
    };

    // Fetch all affiliates with counts based on current filters
    useEffect(() => {
        // Set mounted flag to true when effect runs
        isMountedRef.current = true;

        const fetchAffiliatesWithCounts = async () => {
            try {
                // Build filters same as fetchLeadsFromServer but without pagination
                const filters = {
                    ...currentFilters,
                    hasAffiliate: true, // Only leads with affiliate field
                    ...(searchTerm.trim() && { searchTerm: searchTerm.trim() })
                };

                // Remove undefined and empty values
                Object.keys(filters).forEach(key => {
                    if (filters[key] === undefined || 
                        filters[key] === null ||
                        (Array.isArray(filters[key]) && filters[key].length === 0) ||
                        (typeof filters[key] === 'string' && filters[key].trim() === '')) {
                        delete filters[key];
                    }
                });

                // Fetch multiple pages to get all affiliates (up to 3000 leads = 300 pages of 10)
                // This ensures we capture all affiliates even if there are many
                const maxPages = 10; // Fetch up to 3000 leads
                const pageSize = 30;
                const affiliateCounts = {};
                let hasMore = true;
                let page = 1;

                while (hasMore && page <= maxPages && isMountedRef.current) {
                    // Check if component is still mounted before each API call
                    if (!isMountedRef.current) {
                        break;
                    }

                    const response = await fetchAvailableLeads(page, pageSize, filters);
                    
                    // Check again after async operation
                    if (!isMountedRef.current) {
                        break;
                    }
                    
                    if (response?.data && response.data.length > 0) {
                        // Count affiliates from fetched leads
                        response.data.forEach(lead => {
                            if (lead.affiliate && typeof lead.affiliate === 'string' && lead.affiliate.trim() !== '') {
                                affiliateCounts[lead.affiliate] = (affiliateCounts[lead.affiliate] || 0) + 1;
                            }
                        });
                        
                        // Check if there are more pages
                        if (response.pagination) {
                            hasMore = page < response.pagination.totalPages;
                        } else {
                            hasMore = response.data.length === pageSize; // If we got a full page, might have more
                        }
                        page++;
                    } else {
                        hasMore = false;
                    }
                }
                
                // Only update state if component is still mounted
                if (isMountedRef.current) {
                    // Convert to array and sort by count (descending)
                    const affiliatesArray = Object.entries(affiliateCounts)
                        .map(([affiliate, count]) => ({ affiliate, count }))
                        .sort((a, b) => b.count - a.count);
                    
                    setAllAffiliatesWithCounts(affiliatesArray);
                }
            } catch (error) {
                // Only handle error if component is still mounted
                if (isMountedRef.current) {
                    console.error("Error fetching affiliates with counts:", error);
                    // Fallback to current page affiliates
                    const affiliateCounts = {};
                    leads.forEach(lead => {
                        if (lead.affiliate && typeof lead.affiliate === 'string' && lead.affiliate.trim() !== '') {
                            affiliateCounts[lead.affiliate] = (affiliateCounts[lead.affiliate] || 0) + 1;
                        }
                    });
                    setAllAffiliatesWithCounts(Object.entries(affiliateCounts).map(([affiliate, count]) => ({ affiliate, count })));
                }
            }
        };

        fetchAffiliatesWithCounts();

        // Cleanup function: set flag to false when component unmounts or dependencies change
        return () => {
            isMountedRef.current = false;
        };
    }, [currentFilters, searchTerm]); // Re-fetch when filters change (removed leads.length dependency)

    // Get unique affiliates for display (from all affiliates with counts)
    const uniqueAffiliates = useMemo(() => {
        return allAffiliatesWithCounts.map(item => item.affiliate);
    }, [allAffiliatesWithCounts]);

    // Handle filter changes
    const handleFilterChange = (key, value) => {
        setCurrentFilters(prev => ({
            ...prev,
            [key]: value
        }));
        setCurrentPage(1); // Reset to page 1 when filters change
    };

    // Handle clear filters
    const handleClearFilters = () => {
        setCurrentFilters({});
        setSearchTerm("");
        setCurrentPage(1);
    };

    // Count active filters
    const activeFiltersCount = useMemo(() => {
        let count = 0;
        if (searchTerm.trim()) count++;
        if (currentFilters.affiliate && currentFilters.affiliate.length > 0) count++;
        if (currentFilters.statuses && currentFilters.statuses.length > 0) count++;
        if (currentFilters.country && currentFilters.country.length > 0) count++;
        if (currentFilters.registeredFrom || currentFilters.registeredTo) count++;
        return count;
    }, [searchTerm, currentFilters]);

    if (user?.position?.role !== "admin") {
        return (
            <div className="container mx-auto px-4 sm:px-6 py-8">
                <Card className="max-w-md mx-auto">
                    <CardContent className="text-center py-8">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Access Denied</h3>
                        <p className="text-gray-600">Administrator privileges required to access this page.</p>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 sm:px-6 py-4">
            <div className="mb-4">
                <div className="flex items-center justify-between mb-3">
                    <div>
                        <h1 className="text-xl font-bold text-gray-900 mb-1">Live Leads</h1>
                        <p className="text-sm text-gray-600">Manage leads submitted by affiliates</p>
                    </div>
                    <div className="flex items-center space-x-2">
                        <Button
                            onClick={fetchLeadsFromServer}
                            variant="outline"
                            disabled={loading}
                            className="h-9 text-sm"
                        >
                            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
                            Refresh
                        </Button>
                        <Button
                            onClick={() => setShowCheckEmailsModal(true)}
                            variant="outline"
                            className="h-9 text-sm"
                        >
                            <Mail className="h-4 w-4 mr-2" />
                            Check Emails
                        </Button>
                        {selectedLeads.length > 0 && (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button
                                        variant="outline"
                                        className="h-9 text-sm"
                                    >
                                        Bulk Actions ({selectedLeads.length})
                                        <ChevronDown className="h-4 w-4 ml-2" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-56">
                                    <DropdownMenuItem
                                        onClick={handleBulkAssign}
                                        disabled={selectedLeads.length === 0}
                                        className="cursor-pointer"
                                    >
                                        <UserPlus className="h-4 w-4 mr-2" />
                                        Bulk Assign
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                        onClick={handleBulkUnassign}
                                        disabled={selectedLeads.length === 0}
                                        className="cursor-pointer"
                                    >
                                        <UserMinus className="h-4 w-4 mr-2" />
                                        Bulk Unassign
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        )}
                    </div>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
                    <Card className="shadow-sm">
                        <CardContent className="p-3">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-xs text-gray-600 font-medium">Total Leads</p>
                                    <p className="text-xl font-bold">
                                        {pagination?.totalCount?.toLocaleString() || leads.length.toLocaleString()}
                                    </p>
                                </div>
                                <Users className="h-7 w-7 text-blue-600" />
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="shadow-sm">
                        <CardContent className="p-3">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-xs text-gray-600 font-medium">Selected</p>
                                    <p className="text-xl font-bold">{selectedLeads.length}</p>
                                </div>
                                <UserPlus className="h-7 w-7 text-indigo-600" />
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="shadow-sm">
                        <CardContent className="p-3">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-xs text-gray-600 font-medium">Affiliates</p>
                                    <p className="text-xl font-bold">{uniqueAffiliates.length}</p>
                                </div>
                                <Users className="h-7 w-7 text-green-600" />
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="shadow-sm">
                        <CardContent className="p-3">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-xs text-gray-600 font-medium">Unassigned</p>
                                    <p className="text-xl font-bold">
                                        {leads.filter(l => !l.agent && !l.desk).length}
                                    </p>
                                </div>
                                <UserPlus className="h-7 w-7 text-orange-600" />
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Affiliate Badges */}
                {uniqueAffiliates.length > 0 && (
                    <div className="mb-3 flex flex-wrap gap-1.5">
                        <span className="text-xs font-semibold text-gray-700">Affiliates:</span>
                        {allAffiliatesWithCounts.map(({ affiliate, count }) => {
                            return (
                                <Badge key={affiliate} variant="outline" className="text-xs">
                                    {affiliate} ({count.toLocaleString()})
                                </Badge>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Integrated Filters */}
            <div className="mb-3 space-y-3">
                {/* Search and Filter Toggle */}
                <div className="flex items-center gap-2">
                    <div className="relative flex-1 max-w-md">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                            placeholder="Search by name, email, phone..."
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setCurrentPage(1);
                            }}
                            className="pl-10 h-9 text-sm"
                        />
                    </div>
                    <Button
                        variant="outline"
                        onClick={() => setShowFilters(!showFilters)}
                        className="flex items-center gap-2 h-9 text-sm"
                    >
                        <Filter className="h-4 w-4" />
                        Filters
                        {activeFiltersCount > 0 && (
                            <Badge variant="secondary" className="ml-1 text-xs">
                                {activeFiltersCount}
                            </Badge>
                        )}
                        {showFilters ? (
                            <ChevronUp className="h-4 w-4" />
                        ) : (
                            <ChevronDown className="h-4 w-4" />
                        )}
                    </Button>
                    {activeFiltersCount > 0 && (
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleClearFilters}
                            className="flex items-center gap-1 h-9 text-sm"
                        >
                            <X className="h-4 w-4" />
                            Clear
                        </Button>
                    )}
                </div>

                {/* Expandable Filter Panel */}
                {showFilters && (
                    <Card className="p-3 shadow-sm">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                            {/* Affiliate Filter */}
                            <div className="space-y-1.5">
                                <Label className="text-xs font-semibold text-gray-700">Affiliate</Label>
                                <MultiSelect
                                    options={affiliateOptions}
                                    selected={currentFilters.affiliate || []}
                                    onChange={(selected) => handleFilterChange('affiliate', selected)}
                                    placeholder="Select affiliates"
                                />
                            </div>

                            {/* Status Filter */}
                            <div className="space-y-1.5">
                                <Label className="text-xs font-semibold text-gray-700">Status</Label>
                                <MultiSelect
                                    options={statusOptions}
                                    selected={currentFilters.statuses || []}
                                    onChange={(selected) => handleFilterChange('statuses', selected)}
                                    placeholder="Select statuses"
                                />
                            </div>

                            {/* Country Filter */}
                            <div className="space-y-1.5">
                                <Label className="text-xs font-semibold text-gray-700">Country</Label>
                                <MultiSelect
                                    options={countryOptions}
                                    selected={currentFilters.country || []}
                                    onChange={(selected) => handleFilterChange('country', selected)}
                                    placeholder="Select countries"
                                />
                            </div>

                            {/* Date Range */}
                            <div className="space-y-1.5">
                                <Label className="text-xs font-semibold text-gray-700">Date Range</Label>
                                <div className="grid grid-cols-2 gap-2">
                                    <div>
                                        <Input
                                            id="dateFrom"
                                            type="date"
                                            value={currentFilters.registeredFrom || ""}
                                            onChange={(e) => handleFilterChange('registeredFrom', e.target.value)}
                                            className="h-9"
                                        />
                                    </div>
                                    <div>
                                        <Input
                                            id="dateTo"
                                            type="date"
                                            value={currentFilters.registeredTo || ""}
                                            onChange={(e) => handleFilterChange('registeredTo', e.target.value)}
                                            className="h-9"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Card>
                )}
            </div>

            {/* Select All Checkbox */}
            {filteredLeads.length > 0 && (
                <div className="mb-4 flex items-center space-x-2">
                    <Checkbox
                        checked={selectAll}
                        onCheckedChange={handleSelectAll}
                    />
                    <label className="text-sm font-medium text-gray-700">
                        Select all {filteredLeads.length} leads on this page
                    </label>
                </div>
            )}

            {/* Leads Table */}
            <LeadsTable
                leads={filteredLeads}
                loading={loading}
                selectedLeads={selectedLeads}
                onSelectLead={handleSelectLead}
                isLeadSelected={isLeadSelected}
                showBulkSelection={true}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onLeadUpdate={fetchLeadsFromServer}
                serverSidePagination={true}
                pagination={pagination}
                currentPage={currentPage}
                onPageChange={setCurrentPage}
                pageSize={pageSize}
                onPageSizeChange={setPageSize}
            />

            {/* Bulk Assign Modal */}
            <BulkAssignModal
                open={showBulkAssignModal}
                onClose={() => setShowBulkAssignModal(false)}
                selectedLeads={selectedLeads}
                onSuccess={handleAssignmentSuccess}
            />

            {/* Bulk Unassign Modal */}
            <BulkUnassignModal
                open={showBulkUnassignModal}
                onClose={() => setShowBulkUnassignModal(false)}
                selectedLeads={selectedLeads}
                onSuccess={handleUnassignmentSuccess}
            />

            {/* Check Emails Modal */}
            <CheckEmailsModal
                open={showCheckEmailsModal}
                onClose={() => setShowCheckEmailsModal(false)}
            />
        </div>
    );
}

